// =============================================================================
// iTravel 2.0 — API Client Singleton
// =============================================================================
// Creates and exports a configured ky instance wired up to the auth store
// and the offline sync store. Network errors are detected and propagated to
// the sync store so the UI can reactively show offline status.
//
// Usage:
//   import { api } from '@/lib/api/instance';
//   const data = await api.get('trips').json();
// =============================================================================

import { useAuthStore } from '@/store/auth.store';
import { useSyncStore } from '@/store/sync.store';
import { createApiClient, type TokenStore } from './client';

// ---------------------------------------------------------------------------
// Token Store Adapter
// ---------------------------------------------------------------------------
// Bridges the auth Zustand store to the TokenStore interface expected by
// the API client factory. Since Zustand stores can be accessed outside of
// React components via `getState()`, this works perfectly for interceptors.
// ---------------------------------------------------------------------------

const tokenStore: TokenStore = {
  getAccessToken: () => useAuthStore.getState().accessToken,
  getRefreshToken: () => useAuthStore.getState().refreshToken,
  setTokens: (accessToken: string, refreshToken: string) => {
    useAuthStore.getState().setTokens({ accessToken, refreshToken });
  },
  clearTokens: () => {
    useAuthStore.getState().logout();
  },
};

// ---------------------------------------------------------------------------
// API Client Instance
// ---------------------------------------------------------------------------

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export const api = createApiClient({
  baseUrl: BASE_URL,
  tokenStore,
  onUnauthorized: () => {
    // Clear auth state and let the AuthGuard handle the redirect
    useAuthStore.getState().logout();
  },
  onServerReachable: () => useSyncStore.getState()._markReachable(),
  onNetworkError: () => {
    console.warn('[Pintravel] Network error detected — app may be offline');

    // Update the sync store so the UI reflects the offline state immediately.
    // This is a best-effort update — if the sync store isn't initialized yet
    // (e.g., during the initial auth check), we just log and move on.
    try {
      const store = useSyncStore.getState();
      if (store.isInitialized) {
        store._updateSyncState({
          network: {
            isOnline: false,
            connectionType: 'none',
            lastOnlineAt: store.syncState.network.lastOnlineAt,
          },
        });
      }
    } catch {
      // Swallow — sync store may not be ready yet
    }
  },
});

export default api;
